/**
 * 組織課題（看板）の API クライアント。
 *
 * 画面も MCP（`mcp-org-issues`）も同じ口（product-api の `/org-issues`）を叩く。**判定は API 側の
 * 1 本**にあるので、ここには上限・必須条件・権限を写さない。ボタンの出し分けは API が返す
 * `permissions` を見て、弾かれたら `ApiError.message` をそのまま出す。
 *
 * 形は `@cortex/domain-org-issues` の型をそのまま使う（`import type` なので Firestore は
 * バンドルに入らない）。画面側で写すと、API に項目を足したときに黙ってずれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードと課題を一覧・作成・更新・移動・共有設定・コメントする API クライアント
 * @graph-connects /org-issues [calls] 組織課題 API
 */

import type {
  Board,
  BoardCreateInput,
  BoardPatch,
  BoardPermissions,
  ColumnCategory,
  Comment,
  HistoryEvent,
  Issue,
  IssueCreateInput,
  IssuePatch,
  IssuePermissions,
  IssueSource,
  MyAction,
  Priority,
  Sharing,
} from '@cortex/domain-org-issues';
import { request } from './_request.js';

/** 返りに載る人（`users.id` → 表示名）。 @graph-connects none */
export interface OrgIssuePerson {
  id: string;
  name: string;
  nickName: string | null;
  email: string;
  isActive: boolean;
}

/** 返りに載る人の表。 @graph-connects none */
export type OrgIssuePeople = Record<string, OrgIssuePerson>;

/** ボードと、それに対してできること。 @graph-connects none */
export interface OrgBoardView {
  board: Board;
  permissions: BoardPermissions;
}

/** 課題と、それに対してできること。 @graph-connects none */
export interface OrgIssueView {
  issue: Issue;
  permissions: IssuePermissions;
}

/** 課題 1 件の詳細。 @graph-connects none */
export interface OrgIssueDetail extends OrgIssueView {
  board: Board;
  comments: Comment[];
  history: HistoryEvent[];
  people: OrgIssuePeople;
}

/** 課題一覧の絞り込み。空の項目は送らない。 @graph-connects none */
export interface OrgIssueFilter {
  columnId?: string;
  category?: ColumnCategory;
  priority?: Priority;
  /** `users.id` / メール / ニックネーム / `me` */
  assignee?: string;
  label?: string;
  pinned?: boolean;
}

/** 週次MTG の投稿から起票するときの起点。 @graph-connects none */
export type TeamMeetingPostSource = Extract<IssueSource, { kind: 'team_meeting_post' }>;

/** 課題の作成の入力。起点を省くと画面から作った課題（`manual`）。 @graph-connects none */
export type OrgIssueCreateRequest = IssueCreateInput & { source?: TeamMeetingPostSource };

/** ピン留め・共有状態の変更。 @graph-connects none */
export interface OrgIssueSharingInput {
  pinned?: boolean;
  sharing?: Sharing;
}

/** 絞り込みをクエリ文字列にする。 @graph-connects none */
function toQuery(filter: OrgIssueFilter): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined) continue;
    const text = String(value).trim();
    if (text !== '') params.set(key, text);
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/** ボードの path。 @graph-connects none */
function boardPath(boardId: string): string {
  return `/org-issues/boards/${encodeURIComponent(boardId)}`;
}

/** 課題の path。 @graph-connects none */
function issuePath(boardId: string, issueId: string): string {
  return `${boardPath(boardId)}/issues/${encodeURIComponent(issueId)}`;
}

/** JSON を載せた書き込み。 @graph-connects none */
function send(method: 'POST' | 'PATCH', body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}

/** 組織課題 API。 @graph-connects none */
export const orgIssuesApi = {
  /** @graph-connects /org-issues/boards [calls] 見られるボードの一覧 */
  listBoards: (): Promise<{ boards: OrgBoardView[]; people: OrgIssuePeople }> =>
    request('/org-issues/boards'),

  /** @graph-connects /org-issues/boards [calls] ボードの作成 */
  createBoard: (input: BoardCreateInput): Promise<OrgBoardView & { people: OrgIssuePeople }> =>
    request('/org-issues/boards', send('POST', input)),

  /** @graph-connects /org-issues/boards/{boardId} [calls] ボードの設定変更 */
  updateBoard: (
    boardId: string,
    patch: BoardPatch,
  ): Promise<OrgBoardView & { people: OrgIssuePeople }> =>
    request(boardPath(boardId), send('PATCH', patch)),

  /** @graph-connects /org-issues/boards/{boardId}/members [calls] 担当の候補 */
  boardMembers: (boardId: string): Promise<{ members: string[]; people: OrgIssuePeople }> =>
    request(`${boardPath(boardId)}/members`),

  /** @graph-connects /org-issues/actions [calls] 自分の未完了 Next */
  myActions: (): Promise<{ actions: MyAction[]; people: OrgIssuePeople }> =>
    request('/org-issues/actions'),

  /** @graph-connects /org-issues/boards/{boardId}/issues [calls] 課題の一覧 */
  listIssues: (
    boardId: string,
    filter: OrgIssueFilter = {},
  ): Promise<{ board: OrgBoardView; issues: OrgIssueView[]; people: OrgIssuePeople }> =>
    request(`${boardPath(boardId)}/issues${toQuery(filter)}`),

  /** @graph-connects /org-issues/boards/{boardId}/issues [calls] 課題の作成 */
  createIssue: (
    boardId: string,
    input: OrgIssueCreateRequest,
  ): Promise<OrgIssueView & { people: OrgIssuePeople }> =>
    request(`${boardPath(boardId)}/issues`, send('POST', { source: { kind: 'manual' }, ...input })),

  /** @graph-connects /org-issues/boards/{boardId}/issues/{issueId} [calls] 課題の詳細 */
  getIssue: (boardId: string, issueId: string): Promise<OrgIssueDetail> =>
    request(issuePath(boardId, issueId)),

  /** @graph-connects /org-issues/boards/{boardId}/issues/{issueId} [calls] 課題の中身の更新 */
  updateIssue: (
    boardId: string,
    issueId: string,
    patch: IssuePatch,
  ): Promise<OrgIssueView & { people: OrgIssuePeople }> =>
    request(issuePath(boardId, issueId), send('PATCH', patch)),

  /** @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/move [calls] 列の移動 */
  moveIssue: (
    boardId: string,
    issueId: string,
    columnId: string,
  ): Promise<OrgIssueView & { people: OrgIssuePeople }> =>
    request(`${issuePath(boardId, issueId)}/move`, send('POST', { columnId })),

  /** @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/sharing [calls] ピン留め・共有状態 */
  setSharing: (
    boardId: string,
    issueId: string,
    input: OrgIssueSharingInput,
  ): Promise<OrgIssueView & { people: OrgIssuePeople }> =>
    request(`${issuePath(boardId, issueId)}/sharing`, send('POST', input)),

  /** @graph-connects /org-issues/boards/{boardId}/issues/{issueId}/comments [calls] コメントの追加 */
  addComment: (
    boardId: string,
    issueId: string,
    body: string,
  ): Promise<Comment & { people: OrgIssuePeople }> =>
    request(`${issuePath(boardId, issueId)}/comments`, send('POST', { body })),
};
